import ParecerReferencialService from '../service/ParecerReferencialService';
import SnackbarQueue from '@ungp/sds-snackbar-queue';
import router from '@/router';
import moment from 'moment';
import ParecerReferencialRenovacaoService from '../service/ParecerReferencialRenovacaoService';
import { ParecerReferencial, ParecerReferencialRenovacao } from '../types';
import { defineStore } from 'pinia';
import { getAxiosError } from '@/plugins/axios';
import { SimNao } from '@/types/enum/SimNao';
import { UploadedFile } from '@/components/sds-file-upload/types';

interface State {
  parecerReferencial: ParecerReferencial;
  listaRenovacao: ParecerReferencialRenovacao[];
  listExtensoesAceitas: string[];
  listaArquivos: Array<File>;
  isIntegracaoCPA: boolean;
  modalAlterarDatas: {
    show: boolean;
    dtAlteracao: string | null;
    dtParecer: string | null;
    dtValidade: string | null;
  };
  showModalConfirmacaoExcluirRenovacao: boolean;
}

export const getInitialParecerReferencial = (): ParecerReferencial => ({
  nuParecer: null,
  nuAnoParecer: moment().format('YYYY'),
  dtParecer: moment().format('DD/MM/yyyy'),
  dtValidade: moment()
    .add(ANOS_VALIDADE_PADRAO_PARECER, 'years')
    .add(DIAS_VALIDADE_PADRAO_PARECER, 'days')
    .format('DD/MM/yyyy'),
  nuAnoProcessoOficial: null,
  nuProcessoOficial: null,
  flMostraInternet: SimNao.FL_SIM,
  deEmenta: null,
  cdParecerReferencial: null,
  cdDocumento: null,
  nmDocumento: null,
  dtVersao: null,
  flCadastroCpa: SimNao.FL_NAO,
});

export const ANOS_VALIDADE_PADRAO_PARECER = 2;
export const DIAS_VALIDADE_PADRAO_PARECER = -1;

export const useCadastroParecerReferencialStore = defineStore(
  'CadastroParecerReferencialStore',
  {
    state: (): State => ({
      parecerReferencial: getInitialParecerReferencial(),
      listaRenovacao: [],
      listExtensoesAceitas: [],
      listaArquivos: [],
      isIntegracaoCPA: false,
      modalAlterarDatas: {
        show: false,
        dtParecer: null,
        dtValidade: null,
        dtAlteracao: moment().format('DD/MM/yyyy'),
      },
      showModalConfirmacaoExcluirRenovacao: false,
    }),
    getters: {
      isEditing: ({ parecerReferencial }) =>
        parecerReferencial.cdParecerReferencial !== null,
      maxDtValidade: ({ parecerReferencial }) =>
        parecerReferencial.dtParecer
          ? moment(parecerReferencial.dtParecer, 'DD/MM/yyyy')
              .add(ANOS_VALIDADE_PADRAO_PARECER, 'years')
              .add(DIAS_VALIDADE_PADRAO_PARECER, 'days')
              .format('DD/MM/yyyy')
          : null,
      maxDtValidadeModalAlterarDatas: ({ modalAlterarDatas }) =>
        modalAlterarDatas.dtParecer
          ? moment(modalAlterarDatas.dtParecer, 'DD/MM/yyyy')
              .add(ANOS_VALIDADE_PADRAO_PARECER, 'years')
              .add(DIAS_VALIDADE_PADRAO_PARECER, 'days')
              .format('DD/MM/yyyy')
          : null,
      acceptExtensions: ({ listExtensoesAceitas }) =>
        listExtensoesAceitas.map((extensao) => `.${extensao}`).join(', '),
      arquivoEnviado({ parecerReferencial }): UploadedFile | null {
        if (
          !parecerReferencial.cdDocumento ||
          !parecerReferencial.nmDocumento
        ) {
          return null;
        }

        return {
          name: parecerReferencial.nmDocumento,
          identifier: parecerReferencial.cdDocumento,
          lastModified: parecerReferencial.dtVersao ?? '',
          hideRemove: this.isIntegracaoCPA,
        };
      },
      listaRenovacaoSemExcluidos({ listaRenovacao }) {
        return listaRenovacao.filter((renovacao) => !renovacao.excluir);
      },
      temRenovacaoMarcadaParaExclusao: ({ listaRenovacao }) =>
        listaRenovacao.some((renovacao) => renovacao.excluir),
    },
    actions: {
      async popularTela() {
        const [{ data: listExtensoesAceitas }] = await Promise.all([
          ParecerReferencialService.findExtensoesAceitas(),
        ]);

        this.$patch({
          listExtensoesAceitas,
        });
      },
      validateForm() {
        let name = '';
        let id = '';

        if (!this.parecerReferencial.nuAnoParecer) {
          name = 'Ano do parecer';
          id = 'nuAnoParecer';
        } else if (!this.parecerReferencial.nuAnoProcessoOficial) {
          name = 'Ano do processo';
          id = 'nuAnoProcessoOficial';
        } else if (!this.parecerReferencial.nuProcessoOficial) {
          name = 'Número do processo';
          id = 'nuProcessoOficial';
        } else if (!this.parecerReferencial.dtParecer) {
          name = 'Aprovação da Presidência';
          id = 'dtParecer';
        } else if (!this.parecerReferencial.dtValidade) {
          name = 'Data de validade';
          id = 'dtValidade';
        } else if (!this.parecerReferencial.deEmenta) {
          name = 'Ementa';
          id = 'deEmenta';
        }

        const focusInput = (id: string) => {
          const element = document.querySelector<HTMLInputElement>(
            `#${id} input`
          );
          element?.focus({ preventScroll: true });
          element?.scrollIntoView({ behavior: 'smooth' });
        };

        if (name) {
          SnackbarQueue.error(`O campo "${name}" é obrigatório.`);
          focusInput(id);
          return false;
        }

        if (
          (!this.isEditing &&
            this.listaArquivos.length === 0 &&
            !this.isIntegracaoCPA) ||
          (this.isEditing &&
            !this.parecerReferencial.cdDocumento &&
            this.listaArquivos.length === 0)
        ) {
          SnackbarQueue.error('O arquivo é obrigatório.');
          return false;
        }

        if (Number(this.parecerReferencial.nuAnoParecer) < 2023) {
          SnackbarQueue.error(
            'O ano do parecer deve ser maior ou igual a 2023.'
          );

          focusInput('nuAnoParecer');
          return false;
        }

        if (
          Number(this.parecerReferencial.nuAnoParecer) >
          Number(moment().format('YYYY'))
        ) {
          SnackbarQueue.error(
            'O ano do parecer não pode ser maior que o ano atual.'
          );
          focusInput('nuAnoParecer');
          return false;
        }

        return true;
      },
      async salvar() {
        if (!this.validateForm()) {
          return;
        }

        if (
          !this.showModalConfirmacaoExcluirRenovacao &&
          this.temRenovacaoMarcadaParaExclusao
        ) {
          this.showModalConfirmacaoExcluirRenovacao = true;
          return;
        }

        this.showModalConfirmacaoExcluirRenovacao = false;

        if (this.isEditing) {
          this.editar();
          return;
        }

        try {
          const { data } = await ParecerReferencialService.cadastrar(
            this.parecerReferencial,
            this.listaArquivos[0]
          );

          SnackbarQueue.success('Parecer referencial salvo com sucesso');

          router.replace({
            path: `/privado/parecer-referencial/cadastro/${data}`,
          });

          this.parecerReferencial.cdParecerReferencial = data;

          await this.buscar();

          if (this.isIntegracaoCPA) {
            setTimeout(() => router.go(-1), 3500);
          }
        } catch (error) {
          SnackbarQueue.error(
            getAxiosError(error, 'Erro ao cadastrar o parecer referencial.')
          );
        }
      },
      async editar() {
        if (!this.isEditing) {
          return;
        }

        try {
          await ParecerReferencialService.editar(
            this.parecerReferencial.cdParecerReferencial!,
            this.parecerReferencial,
            this.listaRenovacao,
            this.listaArquivos[0]
          );

          SnackbarQueue.success('Parecer referencial salvo com sucesso');

          this.buscar();
        } catch (error) {
          SnackbarQueue.error(
            getAxiosError(error, 'Erro ao editar o parecer referencial')
          );
        }
      },
      async buscar() {
        if (!this.parecerReferencial.cdParecerReferencial) {
          throw new Error('Código do parecer referencial não encontrado.');
        }

        const [{ data: parecerReferencial }, { data: listaRenovacao }] =
          await Promise.all([
            ParecerReferencialService.buscar(
              this.parecerReferencial.cdParecerReferencial
            ),
            ParecerReferencialRenovacaoService.listar(
              this.parecerReferencial.cdParecerReferencial
            ),
          ]);

        this.listaArquivos = [];

        this.parecerReferencial = parecerReferencial;
        this.listaRenovacao = listaRenovacao;

        this.parecerReferencial.dtValidade = null;
        setTimeout(() => {
          this.parecerReferencial.dtValidade =
            this.listaRenovacao[0].dtValidade;
        }, 10);
      },
      async calcularDtValidade(isModal = false) {
        const state = isModal
          ? this.modalAlterarDatas
          : this.parecerReferencial;

        const dtValidade = moment(state.dtParecer, 'DD/MM/yyyy')
          .add(ANOS_VALIDADE_PADRAO_PARECER, 'years')
          .add(DIAS_VALIDADE_PADRAO_PARECER, 'days')
          .format('DD/MM/yyyy');

        state.dtValidade = null;
        setTimeout(() => {
          state.dtValidade = dtValidade;
        }, 0);
      },
      async adicionarAlteracaoDatas() {
        if (!this.modalAlterarDatas.dtParecer) {
          SnackbarQueue.error(
            'O campo "Aprovação da Presidência" é obrigatório.'
          );
          return;
        }

        if (!this.modalAlterarDatas.dtValidade) {
          SnackbarQueue.error('O campo "Data de validade" é obrigatório.');
          return;
        }

        this.listaRenovacao.unshift({
          deSetor: '',
          deUsuario: '',
          dtParecer: this.modalAlterarDatas.dtParecer,
          dtValidade: this.modalAlterarDatas.dtValidade,
          dtRenovacao: moment().format('DD/MM/yyyy'),
        });

        this.modalAlterarDatas.show = false;
      },
      async removerArquivo() {
        this.parecerReferencial.cdDocumento = null;
        this.parecerReferencial.nmDocumento = null;
        this.parecerReferencial.dtVersao = null;
      },
      async validarForaUso() {
        if (this.parecerReferencial.flForaUso === SimNao.FL_SIM) {
          this.parecerReferencial.flMostraInternet = SimNao.FL_NAO;
        }
      },
      /**
       * Integração realizada com o CPA que permite o envio de dados para o GCN
       * por meio de parâmetros de URL codificados em base64.
       * @param dadosCPA os parâmetros de URL codificados em base64
       */
      async parseDadosCPA(dadosCPA: string) {
        const parecerReferencial = JSON.parse(
          decodeURIComponent(escape(atob(dadosCPA)))
        ) as Partial<ParecerReferencial>;

        this.parecerReferencial = {
          ...this.parecerReferencial,
          ...parecerReferencial,
        };

        this.isIntegracaoCPA = true;
      },
    },
  }
);
